import { contentTags, ContentType } from "src/views/assistant/types";
import { StateCreator } from "zustand";
import { StreamingState } from "./StreamingStore";
import { getContentTypeFromTag } from "../services/messageParserUtils";

export type MessageParserSlice = {
  messageParsingState: Map<
    string,
    { currentBlock: ContentType; isThinking: boolean }
  >;
  resetParsingState: () => void;
  determineChunkType: (chunk: string) => ContentType;
  parseIncomingChunk: (
    chunk: string,
    messageId: string,
  ) => { type: ContentType; text: string }[];
  determineThinkingState: (
    parts: { type: ContentType; text: string }[],
    currentThinkingState?: boolean,
  ) => boolean;
  resetMessageParsingState: (messageId: string) => void;
  getMessageParsingState: (messageId: string) => {
    currentBlock: ContentType;
    isThinking: boolean;
  };
};

export const createMessageParserSlice: StateCreator<
  StreamingState,
  [["zustand/subscribeWithSelector", never]],
  [],
  MessageParserSlice
> = (set, get) => ({
  messageParsingState: new Map(),

  resetParsingState: () =>
    set({
      messageParsingState: new Map(),
    }),

  getMessageParsingState: (messageId: string) => {
    const { messageParsingState } = get();
    return (
      messageParsingState.get(messageId) || {
        currentBlock: ContentType.TEXT,
        isThinking: false,
      }
    );
  },

  resetMessageParsingState: (messageId: string) => {
    const { messageParsingState } = get();
    messageParsingState.delete(messageId);
    set({ messageParsingState: new Map(messageParsingState) });
  },

  determineChunkType: (chunk) => {
    for (const tag in contentTags) {
      if (chunk.startsWith(`<${tag}>`)) {
        return getContentTypeFromTag(tag);
      }
    }
    return ContentType.TEXT;
  },

  parseIncomingChunk: (chunk, messageId) => {
    const { determineChunkType, messageParsingState, getMessageParsingState } =
      get();

    // Guard against null/undefined chunks
    if (!chunk) {
      return [];
    }

    const msgState = getMessageParsingState(messageId);
    const blockState = msgState.currentBlock;
    const thinkingState = msgState.isThinking;

    const parts = [];
    const detectedType = determineChunkType(chunk);

    if (detectedType !== ContentType.TEXT) {
      messageParsingState.set(messageId, {
        currentBlock: detectedType,
        isThinking: detectedType === ContentType.THINK ? true : thinkingState,
      });
      set({ messageParsingState: new Map(messageParsingState) });
      parts.push({ type: detectedType, text: chunk });
    } else if (blockState !== ContentType.TEXT) {
      parts.push({ type: blockState, text: chunk });

      const endTag = `</${Object.keys(contentTags).find(
        (k) => contentTags[k] === blockState,
      )}>`;

      if (chunk.includes(endTag)) {
        messageParsingState.set(messageId, {
          currentBlock: ContentType.TEXT,
          isThinking: blockState === ContentType.THINK ? false : thinkingState,
        });
        set({ messageParsingState: new Map(messageParsingState) });
      }
    } else {
      // In TEXT state - check if chunk is an orphaned </tool_response> sentinel
      // The backend may send closing tags separately as signal events (see ToolResponse.tsx)
      const trimmedChunk = chunk.trim();
      const isToolResponseSentinel = trimmedChunk === "</tool_response>";

      // Skip orphaned </tool_response> sentinels - they shouldn't be rendered as text
      if (!isToolResponseSentinel) {
        parts.push({ type: ContentType.TEXT, text: chunk });
      }
    }

    return parts;
  },

  determineThinkingState: (parts, currentThinkingState = false) => {
    let isThinking = currentThinkingState;

    for (const part of parts) {
      if (part.type === ContentType.THINK) {
        if (part.text.includes("<think>")) {
          isThinking = true;
        }
        if (part.text.includes("</think>")) {
          isThinking = false;
        }
      }
    }

    return isThinking;
  },
});
